"use client";

/**
 * Deploy-skew recovery for Next.js Server Actions (IDD-18).
 *
 * When a browser holds a client bundle from a previous deployment and posts a
 * Server Action, the running server build may no longer have that action id.
 * Next.js then throws:
 *
 *   Error: Failed to find Server Action "…".
 *   This request might be from an older or newer deployment.
 *
 * The structural fix is a stable NEXT_SERVER_ACTIONS_ENCRYPTION_KEY + pinned
 * generateBuildId (so action ids survive redeploys). This module is the
 * defence-in-depth layer: on the rare genuine skew (e.g. the user's tab spans
 * an action-code change), detect the signature and self-heal with a single
 * guarded reload so the user pulls the current bundle instead of seeing a
 * crash + a console full of errors.
 */

/** True when `err` is Next's stale/missing Server Action deploy-skew error. */
export function isStaleServerActionError(err: unknown): boolean {
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "";
  return (
    /Failed to find Server Action/i.test(msg) ||
    /older or newer deployment/i.test(msg)
  );
}

const RELOAD_GUARD_KEY = "dp:staleActionReloadAt";
// Debounce so a (hypothetical) still-broken bundle can't hot-loop reloads.
const RELOAD_DEBOUNCE_MS = 30_000;

/**
 * Reload once to fetch the current bundle. Guarded via sessionStorage so we
 * never enter a reload loop if the fresh bundle somehow still mismatches.
 */
export function recoverFromStaleServerAction(): void {
  if (typeof window === "undefined") return;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || "0");
    const now = Date.now();
    if (now - last < RELOAD_DEBOUNCE_MS) return; // already reloaded just now
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — fall through to a
    // single best-effort reload rather than looping.
  }
  window.location.reload();
}

let installed = false;

/**
 * Install always-on window listeners that catch the skew error wherever it is
 * thrown (imperative `await action()` calls surface via unhandledrejection;
 * render-path throws via the error event) and self-heal. Idempotent.
 */
export function installStaleServerActionRecovery(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (ev: ErrorEvent) => {
    if (isStaleServerActionError(ev.error ?? ev.message)) {
      recoverFromStaleServerAction();
    }
  });
  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    if (isStaleServerActionError(ev.reason)) {
      recoverFromStaleServerAction();
    }
  });
}
